'use client';

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { authService } from '@/lib/authService';
import type { ChildEntryType } from '../../types';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useProcurementSearch');

export interface Supplier {
  id: number;
  name: string;
  company_name: string | null;
}

export interface PurchaseOrder {
  id: string;
  item_number: string;
  title: string;
  contact_name: string;
}

export interface PoLineItem {
  description: string;
  quantity: number;
  quantity_received: number;
  unit_price: number;
  total: number;
}

export interface PoFulfillment {
  totalOrdered: number;
  totalReceived: number;
  totalRemaining: number;
  isFullyDelivered: boolean;
  isPartiallyDelivered: boolean;
  lineItems: Array<{
    description: string;
    orderedQty: number;
    receivedQty: number;
    remainingQty: number;
    unit_price: number;
  }>;
}

interface UseProcurementSearchParams {
  selectedEntryType: ChildEntryType | null;
  isProcurementType: boolean;
  requiresPurchaseOrder: boolean;
  isOpen: boolean;
}

export function useProcurementSearch({
  selectedEntryType,
  isProcurementType,
  requiresPurchaseOrder,
  isOpen,
}: UseProcurementSearchParams) {
  const [allSuppliers, setAllSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [showSupplierSearch, setShowSupplierSearch] = useState(false);

  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [poLineItems, setPoLineItems] = useState<PoLineItem[]>([]);
  const [poSearchQuery, setPoSearchQuery] = useState('');
  const [poSearchResults, setPoSearchResults] = useState<PurchaseOrder[]>([]);
  const [isSearchingPO, setIsSearchingPO] = useState(false);
  const [showPOSearch, setShowPOSearch] = useState(false);

  const [procurementSetupExpanded, setProcurementSetupExpanded] = useState(true);
  const [linkToSupplier, setLinkToSupplier] = useState(false);

  const supplierDropdownRef = useRef<HTMLDivElement>(null);
  const poDropdownRef = useRef<HTMLDivElement>(null);
  const prevPoLineItemsLengthRef = useRef(0);
  const standardAutoCollapseRef = useRef(false);

  const filteredSuppliers = useMemo(() => {
    if (!supplierSearchQuery.trim()) return allSuppliers;
    const q = supplierSearchQuery.toLowerCase();
    return allSuppliers.filter(
      (s) =>
        (s.company_name && s.company_name.toLowerCase().includes(q)) ||
        s.name.toLowerCase().includes(q)
    );
  }, [allSuppliers, supplierSearchQuery]);

  const poFulfillment = useMemo<PoFulfillment | null>(() => {
    if (poLineItems.length === 0) return null;
    const totalOrdered = poLineItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalReceived = poLineItems.reduce((sum, item) => sum + item.quantity_received, 0);
    const totalRemaining = totalOrdered - totalReceived;
    return {
      totalOrdered,
      totalReceived,
      totalRemaining,
      isFullyDelivered: totalOrdered > 0 && totalRemaining <= 0,
      isPartiallyDelivered: totalReceived > 0 && totalRemaining > 0,
      lineItems: poLineItems.map((item) => ({
        description: item.description,
        orderedQty: item.quantity,
        receivedQty: item.quantity_received,
        remainingQty: Math.max(0, item.quantity - item.quantity_received),
        unit_price: item.unit_price || 0,
      })),
    };
  }, [poLineItems]);

  const loadSuppliers = useCallback(async () => {
    if (allSuppliers.length > 0) return;
    setLoadingSuppliers(true);
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(
        '/api/v1/contacts?contact_types=supplier&is_active=true&limit=200',
        { headers: { Authorization: `Bearer ${token || ''}` } }
      );
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        setAllSuppliers(
          data.data.map((c: { id?: number; company_name?: string | null; contact_person?: string | null }) => ({
            id: Number(c.id) || 0,
            name: String(c.contact_person || c.company_name || ''),
            company_name: c.company_name || null,
          }))
        );
      }
    } catch (error) {
      if (!isSilentFetchError(error)) logger.warn('[useProcurementSearch] Failed to load suppliers:', error);
    } finally {
      setLoadingSuppliers(false);
    }
  }, [allSuppliers.length]);

  const isPurchaseWithSupplier = selectedEntryType === 'purchase' && linkToSupplier;

  // Auto-load suppliers when a procurement type is selected
  useEffect(() => {
    if ((isProcurementType || isPurchaseWithSupplier) && allSuppliers.length === 0) {
      void loadSuppliers();
    }
  }, [isProcurementType, isPurchaseWithSupplier, allSuppliers.length, loadSuppliers]);

  const searchPurchaseOrders = useCallback(async (query: string, supplierId?: number) => {
    if (!query.trim() && !supplierId) {
      setPoSearchResults([]);
      return;
    }
    setIsSearchingPO(true);
    try {
      const token = authService.getTokens()?.access_token;
      const params = new URLSearchParams({ action: 'purchase-orders', status: 'open', limit: '20' });
      if (query.trim()) params.set('search', query.trim());
      if (supplierId) params.set('supplier_id', String(supplierId));
      const response = await fetch(`/api/v1/procurement?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        const filteredResults = data.data.filter((po: { delivery_status?: string; status?: string; is_partial_delivery?: boolean }) => {
          if (po.status === 'closed' || po.status === 'completed') return false;
          if (selectedEntryType === 'delivery_note' && po.delivery_status === 'received' && !po.is_partial_delivery) return false;
          return true;
        });
        setPoSearchResults(filteredResults);
      } else {
        setPoSearchResults([]);
      }
    } catch (error) {
      if (!isSilentFetchError(error)) logger.warn('[useProcurementSearch] PO search failed:', error);
      setPoSearchResults([]);
    } finally {
      setIsSearchingPO(false);
    }
  }, [selectedEntryType]);

  const loadPoLineItems = useCallback(async (poId: string) => {
    try {
      const token = authService.getTokens()?.access_token;
      const response = await fetch(`/api/v1/work-items/${poId}/bom`, {
        headers: { Authorization: `Bearer ${token || ''}` },
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.data?.items)) {
        setPoLineItems(
          data.data.items.map((item: { item_name?: string; description?: string; quantity?: string; quantity_received?: string; unit_cost?: string; total_cost?: string }) => ({
            description: String(item.item_name || item.description || ''),
            quantity: Number(item.quantity) || 0,
            quantity_received: Number(item.quantity_received) || 0,
            unit_price: Number(item.unit_cost) || 0,
            total: Number(item.total_cost) || 0,
          }))
        );
      } else {
        setPoLineItems([]);
      }
    } catch (error) {
      if (!isSilentFetchError(error)) logger.warn('[useProcurementSearch] Failed to load PO line items:', error);
      setPoLineItems([]);
    }
  }, []);

  useEffect(() => {
    if (selectedPurchaseOrder) {
      void loadPoLineItems(selectedPurchaseOrder.id);
    } else {
      setPoLineItems([]);
    }
  }, [selectedPurchaseOrder, loadPoLineItems]);

  // Debounced PO search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedSupplier && poSearchQuery.length >= 2) {
        void searchPurchaseOrders(poSearchQuery, selectedSupplier.id);
      } else if (selectedSupplier && !poSearchQuery.trim()) {
        void searchPurchaseOrders('', selectedSupplier.id);
      } else {
        setPoSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [poSearchQuery, selectedSupplier, searchPurchaseOrders]);

  // Auto-collapse procurement setup when PO line items load for the first time
  useEffect(() => {
    if (
      selectedPurchaseOrder &&
      poLineItems.length > 0 &&
      prevPoLineItemsLengthRef.current === 0 &&
      procurementSetupExpanded
    ) {
      setProcurementSetupExpanded(false);
    }
    prevPoLineItemsLengthRef.current = poLineItems.length;
  }, [selectedPurchaseOrder, poLineItems.length, procurementSetupExpanded]);

  // In standard mode (no PO required), auto-collapse when supplier is first selected
  useEffect(() => {
    if (
      isProcurementType &&
      !requiresPurchaseOrder &&
      selectedSupplier &&
      !selectedPurchaseOrder
    ) {
      if (!standardAutoCollapseRef.current && procurementSetupExpanded) {
        standardAutoCollapseRef.current = true;
        setProcurementSetupExpanded(false);
      }
    } else {
      standardAutoCollapseRef.current = false;
    }
  }, [isProcurementType, requiresPurchaseOrder, selectedSupplier, selectedPurchaseOrder, procurementSetupExpanded]);

  // Close dropdowns on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(event.target as Node)) {
        setShowSupplierSearch(false);
      }
      if (poDropdownRef.current && !poDropdownRef.current.contains(event.target as Node)) {
        setShowPOSearch(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const reset = useCallback(() => {
    setSelectedSupplier(null);
    setSupplierSearchQuery('');
    setShowSupplierSearch(false);
    setLinkToSupplier(false);
    setSelectedPurchaseOrder(null);
    setPoLineItems([]);
    setProcurementSetupExpanded(true);
    setPoSearchQuery('');
    setPoSearchResults([]);
    setIsSearchingPO(false);
    setShowPOSearch(false);
    setAllSuppliers([]);
    setLoadingSuppliers(false);
    standardAutoCollapseRef.current = false;
    prevPoLineItemsLengthRef.current = 0;
  }, []);

  return {
    allSuppliers,
    loadingSuppliers,
    selectedSupplier,
    setSelectedSupplier,
    supplierSearchQuery,
    setSupplierSearchQuery,
    showSupplierSearch,
    setShowSupplierSearch,
    filteredSuppliers,
    selectedPurchaseOrder,
    setSelectedPurchaseOrder,
    poLineItems,
    poSearchQuery,
    setPoSearchQuery,
    poSearchResults,
    isSearchingPO,
    showPOSearch,
    setShowPOSearch,
    procurementSetupExpanded,
    setProcurementSetupExpanded,
    linkToSupplier,
    setLinkToSupplier,
    isPurchaseWithSupplier,
    poFulfillment,
    supplierDropdownRef,
    poDropdownRef,
    reset,
  };
}
